import React, { useState, useRef } from 'react';
import { OBJToGLBConverter } from '../lib/obj-to-glb-converter';

interface FileWithPreview extends File {
    preview?: string;
}

export const OBJToGLBConverterComponent: React.FC = () => {
    const [objFile, setObjFile] = useState<FileWithPreview | null>(null);
    const [mtlFile, setMtlFile] = useState<File | null>(null);
    const [textureFiles, setTextureFiles] = useState<File[]>([]);
    const [isConverting, setIsConverting] = useState(false);
    const [conversionProgress, setConversionProgress] = useState<string>('');

    const objInputRef = useRef<HTMLInputElement>(null);
    const mtlInputRef = useRef<HTMLInputElement>(null);
    const textureInputRef = useRef<HTMLInputElement>(null);

    const converter = new OBJToGLBConverter();

    const handleOBJFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file && file.name.toLowerCase().endsWith('.obj')) {
            setObjFile(file);
        } else {
            alert('Please select a valid OBJ file');
        }
    };

    const handleMTLFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file && file.name.toLowerCase().endsWith('.mtl')) {
            setMtlFile(file);
        } else {
            alert('Please select a valid MTL file');
        }
    };

    const handleTextureFilesSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files || []);
        const validFiles = files.filter(file =>
            /\.(jpg|jpeg|png|bmp|tga)$/i.test(file.name)
        );
        setTextureFiles(validFiles);
    };

    const removeTextureFile = (index: number) => {
        setTextureFiles(prev => prev.filter((_, i) => i !== index));
    };

    const convertToGLB = async () => {
        if (!objFile) {
            alert('Please select an OBJ file first');
            return;
        }

        setIsConverting(true);
        setConversionProgress('Starting conversion...');

        try {
            setConversionProgress('Loading OBJ file...');
            const glbData = await converter.convertGarmentAsset(objFile, mtlFile, textureFiles);

            setConversionProgress('Downloading GLB file...');
            const outputName = objFile.name.replace('.obj', '.glb');
            converter.saveGLB(glbData, outputName);

            setConversionProgress('Conversion completed successfully!');
            setTimeout(() => setConversionProgress(''), 3000);

        } catch (error) {
            console.error('Conversion error:', error);
            setConversionProgress(`Conversion failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
        } finally {
            setIsConverting(false);
        }
    };

    const clearAll = () => {
        setObjFile(null);
        setMtlFile(null);
        setTextureFiles([]);
        setConversionProgress('');
        if (objInputRef.current) objInputRef.current.value = '';
        if (mtlInputRef.current) mtlInputRef.current.value = '';
        if (textureInputRef.current) textureInputRef.current.value = '';
    };

    return (
        <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-lg">
            <h2 className="text-2xl font-bold mb-6 text-center">OBJ to GLB Converter</h2>

            {/* OBJ File Input */}
            <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    OBJ File (Required)
                </label>
                <input
                    ref={objInputRef}
                    type="file"
                    accept=".obj"
                    onChange={handleOBJFileSelect}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
                {objFile && (
                    <p className="mt-1 text-sm text-green-600">✓ {objFile.name}</p>
                )}
            </div>

            {/* MTL File Input */}
            <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    MTL File (Optional)
                </label>
                <input
                    ref={mtlInputRef}
                    type="file"
                    accept=".mtl"
                    onChange={handleMTLFileSelect}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
                {mtlFile && (
                    <p className="mt-1 text-sm text-green-600">✓ {mtlFile.name}</p>
                )}
            </div>

            {/* Texture Files Input */}
            <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Texture Files (Optional)
                </label>
                <input
                    ref={textureInputRef}
                    type="file"
                    accept=".jpg,.jpeg,.png,.bmp,.tga"
                    multiple
                    onChange={handleTextureFilesSelect}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
                {textureFiles.length > 0 && (
                    <div className="mt-2 space-y-1">
                        {textureFiles.map((file, index) => (
                            <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                                <span className="text-sm text-green-600">✓ {file.name}</span>
                                <button
                                    onClick={() => removeTextureFile(index)}
                                    className="text-red-500 hover:text-red-700 text-sm"
                                >
                                    ✕
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Progress Display */}
            {conversionProgress && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
                    <p className="text-sm text-blue-800">{conversionProgress}</p>
                </div>
            )}

            {/* Action Buttons */}
            <div className="flex space-x-4">
                <button
                    onClick={convertToGLB}
                    disabled={!objFile || isConverting}
                    className={`flex-1 py-2 px-4 rounded-md font-medium ${
                        !objFile || isConverting
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                >
                    {isConverting ? 'Converting...' : 'Convert to GLB'}
                </button>

                <button
                    onClick={clearAll}
                    className="px-4 py-2 border border-gray-300 rounded-md font-medium text-gray-700 hover:bg-gray-50"
                >
                    Clear All
                </button>
            </div>

            {/* Features Info */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-sm font-semibold text-gray-800 mb-2">Features:</h3>
                <ul className="text-xs text-gray-600 space-y-1">
                    <li>• Client-side processing (files never leave your browser)</li>
                    <li>• Zero texture loss - automatically packages PBR materials</li>
                    <li>• Instant file size optimization</li>
                    <li>• Preserves UV coordinates and vertex normals</li>
                    <li>• Supports multiple texture formats</li>
                </ul>
            </div>
        </div>
    );
};